import { c as createIsolatedRegistry, r as createLogger } from "../logger-CdXeOlrX.js";
import { probeImmersiveArSupportOutcome } from "../ar/webxr-support-probe.js";
import { geolocationPermissionState, queryPermissionState } from "./permission-state.js";
//#region ../src/sensors/permission-checker.ts
/**
* Permission Checker Module
*
* Provides utilities to check and verify mandatory device permissions
* required for AR+GPS tracking: WebXR (immersive-ar), Camera, and Geolocation.
*
* This module is used by the setup modal to verify permissions BEFORE
* the user enters AR mode, providing clear feedback on what's missing.
*/
const log = createLogger("PermissionChecker");
let fileSystemState = {
	folderSelected: false,
	writeVerified: false
};
/**
* Reset file system permission state.
* Exported for testing purposes.
* @internal
*/
function resetFileSystemState() {
	fileSystemState = {
		folderSelected: false,
		writeVerified: false
	};
}
/**
* Set file system permission state.
* Used by the storage module after folder selection and write verification.
* @internal
*/
function setFileSystemState(state) {
	fileSystemState = {
		...fileSystemState,
		...state
	};
}
/**
* Check file system access permission status.
*
* User Feedback Issue #1: File system access is critical for recording.
* This function checks:
* 1. If OPFS (Origin Private File System) is available
* 2. If storage has been initialized
* 3. If write access has been verified
*
* Note: We now use OPFS instead of showDirectoryPicker because it works
* consistently on Android Chrome, iOS Safari, and Desktop browsers.
*
* @returns Permission status for file system access
*/
function checkFileSystemPermission() {
	if (typeof navigator === "undefined" || !navigator.storage || typeof navigator.storage.getDirectory !== "function") return {
		supported: false,
		granted: null,
		error: "Origin Private File System (OPFS) not supported. Use Chrome 86+, Safari 15.2+, or Firefox 111+."
	};
	if (!fileSystemState.folderSelected) return {
		supported: true,
		granted: null
	};
	if (!fileSystemState.writeVerified) return {
		supported: true,
		granted: false,
		error: fileSystemState.writeError || "Storage write failed. Please try again."
	};
	return {
		supported: true,
		granted: true
	};
}
/**
* Check if WebXR immersive-ar mode is supported.
* Does not request a session, only checks support.
*/
async function checkWebXRSupport() {
	if (!navigator.xr) return {
		supported: false,
		granted: null,
		error: "WebXR API not available. Use a compatible browser like Chrome on Android."
	};
	switch (await probeImmersiveArSupportOutcome()) {
		case "supported": return {
			supported: true,
			granted: true
		};
		case "unsupported": return {
			supported: false,
			granted: null,
			error: "AR mode not supported. Ensure you have ARCore/ARKit installed."
		};
		default: return {
			supported: false,
			granted: null,
			error: "Failed to check AR support. Please refresh and try again."
		};
	}
}
/**
* Shared Permissions-API status checker (quality-review C-2): probe-free
* query with the standard supported/granted/denied/prompt mapping. The two
* former copies (geolocation/camera) differed only in the API guard and the
* user-facing strings. The query itself is `permission-state.ts`'s, the one
* implementation the globe lab reads too.
*/
async function checkPermissionViaQuery(options) {
	if (!options.apiAvailable) return {
		supported: false,
		granted: null,
		error: options.unsupportedError
	};
	const state = await options.queryState();
	if (state === "granted") return {
		supported: true,
		granted: true
	};
	if (state === "denied") return {
		supported: true,
		granted: false,
		error: options.deniedError
	};
	return {
		supported: true,
		granted: null
	};
}
/**
* Check geolocation permission status without triggering a prompt.
* Uses the Permissions API if available, otherwise returns unknown state.
*/
async function checkGeolocationPermission() {
	return checkPermissionViaQuery({
		queryState: geolocationPermissionState,
		apiAvailable: !!navigator.geolocation,
		unsupportedError: "Geolocation API not available in this browser.",
		deniedError: "Location access denied. Please enable in browser settings."
	});
}
/**
* Request geolocation permission by triggering a position request.
* This will show the browser's permission prompt if not already granted.
*
* @param timeoutMs - Maximum time to wait for position (default 10s)
* @returns Updated permission status after request
*/
async function requestGeolocationPermission(timeoutMs = 1e4) {
	if (!navigator.geolocation) return {
		supported: false,
		granted: null,
		error: "Geolocation API not available in this browser."
	};
	return new Promise((resolve) => {
		navigator.geolocation.getCurrentPosition(() => {
			log.info("Geolocation permission granted");
			resolve({
				supported: true,
				granted: true
			});
		}, (error) => {
			log.warn("Geolocation error:", error.message);
			if (error.code === error.PERMISSION_DENIED) resolve({
				supported: true,
				granted: false,
				error: "Location access denied. Please enable in browser settings."
			});
			else if (error.code === error.POSITION_UNAVAILABLE) resolve({
				supported: true,
				granted: null,
				error: "Location unavailable. Check GPS is enabled on your device."
			});
			else resolve({
				supported: true,
				granted: null,
				error: "Location request timed out. Please try again."
			});
		}, {
			enableHighAccuracy: true,
			timeout: timeoutMs,
			maximumAge: 0
		});
	});
}
/**
* Check camera permission status without triggering a prompt.
* Uses the Permissions API if available.
*/
async function checkCameraPermission() {
	return checkPermissionViaQuery({
		queryState: () => queryPermissionState("camera"),
		apiAvailable: !!navigator.mediaDevices?.getUserMedia,
		unsupportedError: "Camera API not available. Use HTTPS and a modern browser.",
		deniedError: "Camera access denied. Please enable in browser settings."
	});
}
/**
* Request camera permission by triggering getUserMedia.
* This will show the browser's permission prompt if not already granted.
*
* @returns Updated permission status after request
*/
async function requestCameraPermission() {
	if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) return {
		supported: false,
		granted: null,
		error: "Camera API not available. Use HTTPS and a modern browser."
	};
	try {
		const stream = await navigator.mediaDevices.getUserMedia({ video: true });
		for (const track of stream.getTracks()) track.stop();
		log.info("Camera permission granted");
		return {
			supported: true,
			granted: true
		};
	} catch (err) {
		log.warn("Camera permission error:", err);
		const error = err;
		if (error.name === "NotAllowedError") return {
			supported: true,
			granted: false,
			error: "Camera access denied. Please enable in browser settings."
		};
		else if (error.name === "NotFoundError") return {
			supported: false,
			granted: null,
			error: "No camera found on this device."
		};
		return {
			supported: true,
			granted: null,
			error: "Camera access failed. Please try again."
		};
	}
}
/**
* DeviceOrientationEvent availability + iOS 13+ permission-API probe
* (quality-review C-2 — this preamble used to be duplicated between the
* check and request paths). `available: false` ⇒ no orientation support at
* all; `requestPermission: null` ⇒ orientation works without a permission.
* The returned function is bound to `DeviceOrientationEvent` (it is a static
* method — an unbound call would lose `this`).
*/
function getIosOrientationPermissionApi() {
	if (typeof DeviceOrientationEvent === "undefined") return { available: false };
	const withPermission = DeviceOrientationEvent;
	return {
		available: true,
		requestPermission: typeof withPermission.requestPermission === "function" ? withPermission.requestPermission.bind(DeviceOrientationEvent) : null
	};
}
/**
* Check device orientation permission status.
* On iOS 13+, this requires explicit permission request.
*/
function checkOrientationPermission() {
	const api = getIosOrientationPermissionApi();
	if (!api.available) return Promise.resolve({
		supported: false,
		granted: null,
		error: "Device orientation not supported."
	});
	if (api.requestPermission) return Promise.resolve({
		supported: true,
		granted: null
	});
	return Promise.resolve({
		supported: true,
		granted: true
	});
}
/**
* Request device orientation permission (iOS 13+).
* On other platforms, this is a no-op that returns granted.
*/
async function requestOrientationPermission() {
	const api = getIosOrientationPermissionApi();
	if (!api.available) return {
		supported: false,
		granted: null,
		error: "Device orientation not supported."
	};
	if (api.requestPermission) try {
		if (await api.requestPermission() === "granted") {
			log.info("Orientation permission granted");
			return {
				supported: true,
				granted: true
			};
		} else return {
			supported: true,
			granted: false,
			error: "Motion & orientation access denied."
		};
	} catch {
		return {
			supported: true,
			granted: null,
			error: "Orientation permission request failed."
		};
	}
	return {
		supported: true,
		granted: true
	};
}
/**
* Check all permissions without triggering any prompts.
* Returns current known state of each permission.
*/
async function checkAllPermissions() {
	const [webxr, geolocation, camera, orientation] = await Promise.all([
		checkWebXRSupport(),
		checkGeolocationPermission(),
		checkCameraPermission(),
		checkOrientationPermission()
	]);
	const fileSystem = checkFileSystemPermission();
	return {
		webxr,
		geolocation,
		camera,
		orientation,
		fileSystem,
		allMandatoryReady: computeAllMandatoryReady({
			webxr,
			geolocation,
			camera,
			fileSystem
		})
	};
}
/**
* The mandatory-set gate, written once (quality-review C-2 — the 8-clause
* boolean used to be spelled out twice and could drift): WebXR, Geolocation,
* Camera and FileSystem must all be supported AND granted. Orientation is
* deliberately not part of the mandatory set.
*/
function computeAllMandatoryReady(result) {
	return [
		result.webxr,
		result.geolocation,
		result.camera,
		result.fileSystem
	].every((p) => p.supported && p.granted === true);
}
/**
* Subscribe to out-of-band permission changes.
*
* Today the recorder app only re-runs `checkAllPermissions()` at startup
* and after the user clicks "Grant Permissions" — so a permission flipped
* via browser settings (e.g. user denies location, then enables it under
* "site settings") is only detected after a full page reload.
*
* This subscription wires three signals:
* 1. `navigator.permissions.query(...)` returns a live `PermissionStatus`
*    for `'geolocation'` and `'camera'`; we keep those references and
*    attach a `'change'` listener via `addEventListener` so we react
*    immediately when the browser updates the underlying state.
* 2. `document.visibilitychange` (only when `visibilityState === 'visible'`)
*    covers the "user tabbed out to settings, toggled, tabbed back" case
*    on browsers where the `'change'` event does not fire reliably.
* 3. `window.focus` covers the same scenario for browsers/standalone
*    windows where `visibilitychange` is not dispatched on focus return.
*
* Each signal triggers a fresh {@link checkAllPermissions} call; the
* resulting {@link PermissionCheckResult} is passed to `callback`.
*
* No periodic polling is performed — the trio above is the complete
* fallback chain. If a browser supports none of these signals for a given
* permission, the user can still re-check by clicking the (now permanently
* visible) "Grant Permissions" button.
*
* @param callback - Invoked with the latest {@link PermissionCheckResult}
*   each time one of the signals fires. The initial state is **not**
*   delivered; callers are expected to have already rendered it.
* @returns A handle whose `unsubscribe()` removes every wired listener.
*/
function subscribePermissionChanges(callback) {
	let disposed = false;
	const cleanups = createIsolatedRegistry({ onError: (err) => log.warn("subscribePermissionChanges: cleanup failed", err) });
	const notify = () => {
		if (disposed) return;
		checkAllPermissions().then((result) => {
			if (!disposed) callback(result);
		}).catch((err) => {
			log.warn("subscribePermissionChanges: re-check failed", err);
		});
	};
	if (typeof navigator !== "undefined" && navigator.permissions && typeof navigator.permissions.query === "function") {
		const wireStatus = (name) => {
			try {
				navigator.permissions.query({ name }).then((status) => {
					if (disposed) return;
					const handler = () => notify();
					status.addEventListener("change", handler);
					cleanups.register(() => status.removeEventListener("change", handler));
				}, () => {});
			} catch {}
		};
		wireStatus("geolocation");
		wireStatus("camera");
	}
	if (typeof document !== "undefined") {
		const onVisibility = () => {
			if (document.visibilityState === "visible") notify();
		};
		document.addEventListener("visibilitychange", onVisibility);
		cleanups.register(() => document.removeEventListener("visibilitychange", onVisibility));
	}
	if (typeof window !== "undefined") {
		const onFocus = () => notify();
		window.addEventListener("focus", onFocus);
		cleanups.register(() => window.removeEventListener("focus", onFocus));
	}
	return { unsubscribe() {
		disposed = true;
		cleanups.runOnce();
	} };
}
/**
* Request all mandatory permissions that haven't been granted yet.
* Shows browser prompts for each permission that needs requesting.
*
* This now includes the WebXR+Depth probe to trigger the "3D map" permission
* upfront, rather than surprising users with a second prompt after "Enter AR".
*
* @returns Updated permission check result after requests
*/
async function requestAllPermissions() {
	const result = await checkAllPermissions();
	if (result.webxr.supported) result.webxr = await requestWebXRWithDepthPermission();
	if (result.camera.supported && result.camera.granted !== true) result.camera = await requestCameraPermission();
	if (result.orientation.supported && result.orientation.granted !== true) result.orientation = await requestOrientationPermission();
	if (result.geolocation.supported && result.geolocation.granted !== true) result.geolocation = await requestGeolocationPermission();
	result.fileSystem = checkFileSystemPermission();
	result.allMandatoryReady = computeAllMandatoryReady(result);
	return result;
}
/**
* Request WebXR permission with depth-sensing by starting a probe session.
*
* The depth-sensing permission (ARCore's "3D map of surroundings") is only
* triggered when an XR session is actually started with depth-sensing options.
* This function starts a minimal probe session to trigger the permission prompt,
* then immediately ends it.
*
* This allows the app to request all AR permissions upfront during the
* setup flow, rather than surprising users with a second prompt when
* they click "Enter AR".
*
* @returns Permission status indicating if AR+depth access was granted
*/
async function requestWebXRWithDepthPermission() {
	if (!navigator.xr) return {
		supported: false,
		granted: null,
		error: "WebXR not available"
	};
	try {
		const session = await navigator.xr.requestSession("immersive-ar", {
			requiredFeatures: ["local-floor"],
			optionalFeatures: ["depth-sensing"],
			depthSensing: {
				usagePreference: ["cpu-optimized"],
				dataFormatPreference: ["luminance-alpha", "float32"]
			}
		});
		try {
			await session.end();
		} catch (endError) {
			log.warn("Probe session end failed (permission still granted):", endError);
		}
		log.info("WebXR with depth-sensing permission granted");
		return {
			supported: true,
			granted: true
		};
	} catch (err) {
		const error = err;
		log.warn("WebXR depth probe failed:", error);
		if (error.name === "NotAllowedError") return {
			supported: true,
			granted: false,
			error: "AR access denied"
		};
		return {
			supported: false,
			granted: null,
			error: error.message
		};
	}
}
//#endregion
export { checkAllPermissions, checkCameraPermission, checkFileSystemPermission, checkGeolocationPermission, checkOrientationPermission, checkWebXRSupport, requestAllPermissions, requestCameraPermission, requestGeolocationPermission, requestOrientationPermission, requestWebXRWithDepthPermission, resetFileSystemState, setFileSystemState, subscribePermissionChanges };
